import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { PieChart, BarChart } from 'echarts/charts';
import { TooltipComponent, LegendComponent, GridComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { formatNumber } from 'helpers/utils';
import { groupProjectsByPipeline, countActiveProjects } from 'helpers/salesPipeline';
import { FaBuilding, FaWeight, FaDollarSign, FaChartPie } from 'react-icons/fa';

echarts.use([PieChart, BarChart, TooltipComponent, LegendComponent, GridComponent, CanvasRenderer]);

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className={`tw-flex tw-items-center tw-gap-3 tw-bg-white tw-rounded-lg tw-border tw-border-slate-100 tw-shadow-sm tw-p-4`}>
    <div className={`tw-w-10 tw-h-10 tw-rounded tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${color}`}>
      <Icon size={18} className="tw-text-white" />
    </div>
    <div className="tw-min-w-0">
      <p className="tw-text-xs tw-text-slate-500 tw-mb-0.5 tw-truncate">{label}</p>
      <p className="tw-text-lg tw-font-bold tw-text-slate-800 tw-mb-0 tw-leading-tight">{value}</p>
      {sub && <p className="tw-text-xs tw-text-slate-400 tw-mb-0">{sub}</p>}
    </div>
  </div>
);

StatCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sub: PropTypes.string,
  color: PropTypes.string.isRequired,
};

const CustomerStats = ({ projects = [] }) => {
  const stats = useMemo(() => {
    const totalProjects = projects.length;
    const totalWeight = projects.reduce((s, p) => s + (parseFloat(p.weight) || 0), 0);
    const totalAmount = projects.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);

    // Breakdown along the sales pipeline (stage order, colour per stage group)
    const pipelineStages = groupProjectsByPipeline(projects);
    const activeProjects = countActiveProjects(projects);

    // Top 6 projects by weight for bar chart
    const topByWeight = [...projects]
      .filter(p => p.weight > 0)
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 6);

    return { totalProjects, totalWeight, totalAmount, activeProjects, pipelineStages, topByWeight };
  }, [projects]);

  const doughnutOption = useMemo(() => ({
    tooltip: {
      trigger: 'item',
      formatter: (p) => {
        const stage = stats.pipelineStages.find(s => s.name === p.name);
        const lines = [
          `<strong>${p.name}</strong>`,
          `${p.value} project${p.value !== 1 ? 's' : ''} (${p.percent}%)`,
        ];
        if (stage?.weight > 0) lines.push(`${formatNumber(stage.weight, 'USD', 1)} MT`);
        if (stage?.amount > 0) lines.push(`$${formatNumber(stage.amount)}`);
        return lines.join('<br/>');
      },
    },
    legend: {
      type: 'scroll',
      orient: 'vertical',
      right: 0,
      top: 'center',
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 6,
      // Pipeline stage names are long — clip them and rely on the hover tooltip.
      textStyle: { fontSize: 11, width: 110, overflow: 'truncate' },
      tooltip: { show: true },
    },
    series: [{
      type: 'pie',
      radius: ['55%', '80%'],
      center: ['32%', '50%'],
      avoidLabelOverlap: false,
      itemStyle: { borderColor: '#fff', borderWidth: 1 },
      label: { show: false },
      labelLine: { show: false },
      data: stats.pipelineStages.map(stage => ({
        name: stage.name,
        value: stage.count,
        itemStyle: { color: stage.color },
      })),
    }],
  }), [stats.pipelineStages]);

  const barOption = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const label = params[0]?.axisValue ?? '';
        const lines = params.map((p) => {
          const v = p.value || 0;
          return p.seriesName === 'Weight (MT)'
            ? `${p.marker} ${p.seriesName}: ${formatNumber(v, 'USD', 1)} MT`
            : `${p.marker} ${p.seriesName}: $${formatNumber(v)}`;
        });
        return `${label}<br/>${lines.join('<br/>')}`;
      },
    },
    legend: { top: 0, itemWidth: 10, itemHeight: 10, textStyle: { fontSize: 11 } },
    grid: { left: 4, right: 4, top: 28, bottom: 2, containLabel: true },
    xAxis: {
      type: 'category',
      data: stats.topByWeight.map(p => p.q_number || p.name?.slice(0, 15) || '—'),
      axisLabel: { fontSize: 10 },
      axisTick: { show: false },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Weight (MT)',
        nameTextStyle: { fontSize: 10 },
        axisLabel: { fontSize: 10 },
        splitLine: { lineStyle: { type: 'dashed' } },
      },
      {
        type: 'value',
        name: 'Amount (USD)',
        position: 'right',
        nameTextStyle: { fontSize: 10 },
        axisLabel: { fontSize: 10, formatter: (v) => `$${(v / 1000).toFixed(0)}K` },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: 'Weight (MT)',
        type: 'bar',
        yAxisIndex: 0,
        data: stats.topByWeight.map(p => p.weight || 0),
        itemStyle: { color: '#3B82F6', borderRadius: [4, 4, 0, 0] },
      },
      {
        name: 'Amount (USD)',
        type: 'bar',
        yAxisIndex: 1,
        data: stats.topByWeight.map(p => p.amount || 0),
        itemStyle: { color: '#10B981', borderRadius: [4, 4, 0, 0] },
      },
    ],
  }), [stats.topByWeight]);

  if (projects.length === 0) return null;

  return (
    <div>
      {/* Stat Cards */}
      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4 tw-mb-4">
        <StatCard
          icon={FaBuilding}
          label="Total Projects"
          value={stats.totalProjects}
          sub="All opportunities"
          color="tw-bg-blue-500"
        />
        <StatCard
          icon={FaChartPie}
          label="Active Projects"
          value={stats.activeProjects}
          sub="Excluding lost deals"
          color="tw-bg-emerald-500"
        />
        <StatCard
          icon={FaWeight}
          label="Total Weight"
          value={`${formatNumber(stats.totalWeight, 'USD', 1)} MT`}
          sub="Across all projects"
          color="tw-bg-amber-500"
        />
        <StatCard
          icon={FaDollarSign}
          label="Total Amount"
          value={`$${formatNumber(stats.totalAmount)}`}
          sub="Contract value (USD)"
          color="tw-bg-violet-500"
        />
      </div>

      {/* Charts */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
        {/* Doughnut — Sales pipeline breakdown */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-100 tw-shadow-sm tw-p-4">
          <p className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide tw-mb-3">
            Projects by Sales Pipeline
          </p>
          <div style={{ height: 160 }}>
            <ReactEChartsCore echarts={echarts} option={doughnutOption} style={{ height: 160 }} notMerge lazyUpdate />
          </div>
        </div>

        {/* Bar — Top Projects by Weight & Amount */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-100 tw-shadow-sm tw-p-4 md:tw-col-span-2">
          <p className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide tw-mb-3">
            Top Projects — Weight &amp; Amount
          </p>
          {stats.topByWeight.length > 0 ? (
            <div style={{ height: 160 }}>
              <ReactEChartsCore echarts={echarts} option={barOption} style={{ height: 160 }} notMerge lazyUpdate />
            </div>
          ) : (
            <div className="tw-h-32 tw-flex tw-items-center tw-justify-center tw-text-slate-400 tw-text-sm">
              No weight data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CustomerStats.propTypes = {
  projects: PropTypes.array,
};

export default CustomerStats;
